import React, { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useHome } from '../../hooks/useHome';
import { useAuth } from '../../hooks/useAuth';
import { useUserSession } from '../../hooks/useUserSession';
import { usePremiumStatus } from '../../hooks/usePremiumStatus';
import { ROUTES } from '../../config/constants';
import {
  HomeNavbar,
  HomeHero,
  HomeFeatures,
  HomeHowItWorks,
  TwitchAppSetup,
  SpotifyAppSetup,
  LoginModal,
  HomeBackground,
  ConnectionStatusSection,
  AppsSectionHeader,
  TwitchAppCard,
  SpotifyAppCard,
  HomeFooter,
  HomeFAQ,
  HOME_TEXTS,
} from '../../components/home';
import '../../App.css';
import '../../styles/Home.css';

/**
 * Composant principal de la page d'accueil
 * Gère l'affichage de la page d'accueil avec les sections de connexion,
 * les applications Twitch/Spotify et les modals de configuration
 *
 * @component
 * @returns {JSX.Element} Le composant de la page d'accueil
 */
function Home() {
  const navigate = useNavigate();

  // Hooks personnalisés pour la gestion de l'état
  const {
    navigateProfile,
    goToDashboard,
    logout,
    logoutSpotify,
    twitchApp,
    showTwitchAppSetup,
    openTwitchAppSetup,
    openTwitchAppEdit,
    closeTwitchAppSetup,
    handleTwitchAppCreated,
    editingTwitchApp,
    spotifyApp,
    showSpotifyAppSetup,
    openSpotifyAppSetup,
    openSpotifyAppEdit,
    closeSpotifyAppSetup,
    handleSpotifyAppCreated,
    editingSpotifyApp,
    showLoginModal,
    openLoginModal,
    closeLoginModal,
    redirectAfterLogin,
  } = useHome();

  const {
    isAuthenticated: isPlatformAuthenticated,
    isLoading: isAuthLoading,
    spotifyUser,
  } = useAuth();

  const { isUserAuthenticated, isLoading: isUserSessionLoading } =
    useUserSession();

  const {
    isPremium,
    isTrial,
    hasPremiumAccess,
    eligibleForTrial,
  } = usePremiumStatus();

  // Calcul de l'état d'authentification combiné
  const isAuthenticated = useMemo(
    () => isUserAuthenticated || isPlatformAuthenticated,
    [isUserAuthenticated, isPlatformAuthenticated]
  );

  const isLoading = useMemo(
    () => isAuthLoading || isUserSessionLoading,
    [isAuthLoading, isUserSessionLoading]
  );

  // Handlers avec gestion d'erreurs
  const handleGoToDashboard = useCallback(() => {
    try {
      navigate(ROUTES.DASHBOARD);
    } catch (error) {
      console.error('Erreur lors de la navigation vers le dashboard:', error);
    }
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  }, [logout]);

  const focusMainContent = useCallback((e) => {
    e.preventDefault();
    const el = document.getElementById('main-content');
    if (el) {
      el.focus();
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  return (
    <div className="home-page">
      <div className="home-skip-links">
        <a
          href="#main-content"
          className="skip-link"
          onClick={focusMainContent}
        >
          {HOME_TEXTS.SKIP_TO_MAIN}
        </a>
        {isAuthenticated && (
          <Link to={ROUTES.DASHBOARD} className="skip-link">
            {HOME_TEXTS.SKIP_TO_DASHBOARD}
          </Link>
        )}
      </div>

      <HomeBackground />

      {/* tabIndex={-1} permet de déplacer le focus clavier depuis le lien d'évitement */}
      <main
        id="main-content"
        className="home-content"
        tabIndex={-1}
      >
        <HomeNavbar
          isAuthenticated={isAuthenticated}
          isAuthLoading={isLoading}
          onLogout={handleLogout}
          isPremium={isPremium}
          isTrial={isTrial}
          hasPremiumAccess={hasPremiumAccess}
          eligibleForTrial={eligibleForTrial}
          onGoToDashboard={handleGoToDashboard}
        />

        <section className="hero-section">
          <HomeHero
            onOpenLogin={openLoginModal}
            onGoToDashboard={handleGoToDashboard}
            isAuthenticated={isAuthenticated}
          />
        </section>

        <HomeFeatures />
        <HomeHowItWorks />

        {!isLoading && !isAuthenticated && (
          <ConnectionStatusSection onLogin={openLoginModal} />
        )}

        {isAuthenticated && (
          <section className="apps-section" id="apps">
            <AppsSectionHeader />
            <div className="user-cards-grid">
              <TwitchAppCard
                twitchApp={twitchApp}
                onDashboard={goToDashboard}
                onProfile={navigateProfile}
                onEdit={openTwitchAppEdit}
                onSetup={openTwitchAppSetup}
              />
              <SpotifyAppCard
                spotifyApp={spotifyApp}
                spotifyUser={spotifyUser}
                onProfile={navigateProfile}
                onLogout={logoutSpotify}
                onEdit={openSpotifyAppEdit}
                onSetup={openSpotifyAppSetup}
              />
            </div>
          </section>
        )}

        <HomeFAQ />
        <HomeFooter />
      </main>

      {showTwitchAppSetup && (
        <TwitchAppSetup
          onAppCreated={handleTwitchAppCreated}
          onCancel={closeTwitchAppSetup}
          existingApp={editingTwitchApp}
        />
      )}

      {showSpotifyAppSetup && (
        <SpotifyAppSetup
          onAppCreated={handleSpotifyAppCreated}
          onCancel={closeSpotifyAppSetup}
          existingApp={editingSpotifyApp}
        />
      )}

      <LoginModal
        isOpen={showLoginModal}
        onClose={closeLoginModal}
        redirectAfterLogin={redirectAfterLogin}
        navigate={navigate}
      />
    </div>
  );
}

export default Home;
